import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Gift, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;
const SIZES = ['S', 'M', 'L', 'XL', '2XL'];
const PROVINCES = ['ON', 'QC', 'BC', 'AB', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'NT', 'YT', 'NU'];

export default function ClaimMerch() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [size, setSize] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [shipping, setShipping] = useState({
    name: '', email: '', address: '', city: '', province: 'ON', postal_code: ''
  });

  const submit = async () => {
    if (!size) { toast.error('Please pick your size'); return; }
    const required = ['name', 'email', 'address', 'city', 'province', 'postal_code'];
    if (required.some((k) => !shipping[k].trim())) {
      toast.error('Please fill in all shipping fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/merch/claim-winner-pack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, size, shipping })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Claim failed');
      setClaimed(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key, label, placeholder, type = 'text') => (
    <div>
      <label className="text-sm font-medium text-neutral-700 mb-1 block">{label}</label>
      <Input type={type} value={shipping[key]} placeholder={placeholder}
        onChange={(e) => setShipping({ ...shipping, [key]: e.target.value })}
        data-testid={`claim-${key}`} className="h-11 rounded-lg" />
    </div>
  );

  if (!token) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-10 text-center" data-testid="claim-invalid-msg">
              <Gift className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>Invalid Claim Link</h2>
              <p className="text-neutral-500 mb-6">This link is missing or expired. Winner tee links arrive by email when you win a giveaway.</p>
              <Link to="/giveaways"><Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl h-11 px-6">Browse Giveaways</Button></Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="claim-merch-card">
            {claimed ? (
              <div className="text-center">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>Your Tee Is On Its Way! 👕</h2>
                <p className="text-neutral-600 mb-8">Size {size}, shipping to {shipping.city}, {shipping.province}. Congrats again, champ.</p>
                <Link to="/giveaways"><Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl h-12 px-8">Back to Giveaways</Button></Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-7 h-7 text-[#FF3B30]" />
                  </div>
                  <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>Claim Your Free Winner Tee</h1>
                  <p className="text-neutral-500 text-sm mt-1">Every Spinser winner gets an official Classic Tee — on us.</p>
                </div>

                <label className="text-sm font-medium text-neutral-700 mb-2 block">Size</label>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="claim-sizes">
                  {SIZES.map((s) => (
                    <button key={s} onClick={() => setSize(s)} data-testid={`claim-size-${s}`}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        size === s ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                      }`}>
                      {s}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {field('name', 'Full Name', 'Jane Winner')}
                  {field('email', 'Email', 'you@email.com', 'email')}
                  {field('address', 'Street Address', '123 Main St')}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">{field('city', 'City', 'Halton Hills')}</div>
                    <div>
                      <label className="text-sm font-medium text-neutral-700 mb-1 block">Province</label>
                      <select value={shipping.province} data-testid="claim-province"
                        onChange={(e) => setShipping({ ...shipping, province: e.target.value })}
                        className="h-11 w-full px-2 rounded-lg border border-neutral-200 text-sm bg-white">
                        {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  {field('postal_code', 'Postal Code', 'L7G 1A1')}
                  <Button onClick={submit} disabled={submitting} data-testid="claim-submit-btn"
                    className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium mt-2">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Claim My Free Tee'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
